// messagingService.js
import { messaging } from "../firebase";   // your firebase.js
import { getToken, onMessage, deleteToken } from "firebase/messaging";
import { registerFCMToken, deactivateFCMToken, clearTokensCache, syncNotificationFromBackend } from "../api/notificationService";
import { extractUserInfo } from "../utils/jwtDecoder";
import { showRealtimeNotificationToast } from "../utils/realtimeNotificationToast";
import {
  buildNotificationNavigationPayload,
  isBackendPersistedNotificationId,
} from "../utils/notificationNavigation";
 
class MessagingService {
 
  constructor() {
    // Auto start receiving messages (same as Angular constructor)
    this.receiveMessaging();
  }
 
  // ------------------------------
  // requestPermission()
  // ------------------------------
  async requestPermission() {
    try {
      // Step 1: Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== "granted") {
        console.error("Notification permission denied:", permission);
        return null;
      }

      // Step 2: Register service worker (if needed) and wait until fully active
      let registration = null;
      if ("serviceWorker" in navigator) {
        try {
          const existingRegistration = await navigator.serviceWorker.getRegistration("/");
          if (!existingRegistration) {
            await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
              scope: "/",
            });
          }
          registration = await navigator.serviceWorker.ready;
        } catch (swError) {
          console.error("Service worker registration failed:", swError);
          return null;
        }
      } else {
        console.error("Service workers are not supported in this browser");
        return null;
      }

      if (!registration) {
        console.error("Service worker is not ready; cannot obtain FCM token");
        return null;
      }

      // Step 3: Get FCM token — only after SW is active (avoids PushManager race)
      const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;
      const tokenOptions = {
        serviceWorkerRegistration: registration,
      };
      
      // Only add vapidKey if provided
      if (vapidKey) {
        tokenOptions.vapidKey = vapidKey;
      }

      const token = await getToken(messaging, tokenOptions);
      
      if (token) {
        //console.log("🔑 FCM Token generated successfully!");
        //console.log("📝 FCM Token (first 50 chars):", token.substring(0, 50) + "...");
        //console.log("💾 Storing FCM token in sessionStorage");
        
        // Store FCM token in sessionStorage (not localStorage) as requested
        sessionStorage.setItem("notificationToken", token);
        
        // Step 4: Register token with backend automatically
        try {
          await this.registerTokenWithBackend(token);
          //console.log("✅ FCM token registered with backend");
        } catch (backendError) {
          console.warn("⚠️ Failed to register FCM token with backend:", backendError);
          // Don't fail the whole process if backend registration fails
          // Token is still stored locally and can be registered later
        }
        
        return token;
      } else {
        console.warn("No FCM token received");
        return null;
      }
    } catch (error) {
      console.error("Error getting FCM token:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      return null;
    }
  }

  // ------------------------------
  // registerTokenWithBackend()
  // ------------------------------
  async registerTokenWithBackend(token) {
    try {
      // Check if user is authenticated before registering
      const authToken = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
      // console.log("token", token);
      
      if (!authToken) {
        //console.log("User not authenticated, skipping backend token registration");
        return null;
      }

      // Register token with backend
      const response = await registerFCMToken(token, true);
      
      if (response && response.success) {
        //console.log("✅ FCM token registered with backend successfully");
        return response;
      } else {
        console.warn("⚠️ Backend token registration returned unsuccessful response");
        return null;
      }
    } catch (error) {
      // Log error but don't throw - token is still stored locally
      console.error("Error registering FCM token with backend:", error);
      throw error;
    }
  }

  // ------------------------------
  // registerTokenAfterLogin()
  // ------------------------------
  // Call this method after successful login to register FCM token
  // Works for all user roles (customer portal, agent, admin, etc.)
  // ALWAYS requests a fresh token to ensure each user gets their own unique token
  async registerTokenAfterLogin() {
    try {
      // Check if user is authenticated
      const authToken = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
      
      if (!authToken) {
        //console.log("User not authenticated, skipping token registration");
        return null;
      }

      // ALWAYS request a fresh token on login to ensure each user gets unique token
      // This is important when multiple users login on the same device
      //console.log("🔑 Requesting fresh FCM token for new user login...");
      
      // Clear any existing token first (from sessionStorage)
      sessionStorage.removeItem("notificationToken");
      
      // Request permission and get fresh token
      const token = await this.requestPermission();
      if (token) {
        //console.log("✅ Fresh FCM token obtained and registered for user");
        return { success: true, token: token };
      }
      
      return null;
    } catch (error) {
      console.error("Error registering FCM token after login:", error);
      // Don't throw - allow login to continue even if token registration fails
      return null;
    }
  }
 
  // ------------------------------
  // receiveMessaging()
  // ------------------------------
  receiveMessaging() {
    if (!messaging) {
      console.warn("⚠️ Firebase messaging not initialized, skipping message listener");
      console.warn("⚠️ FCM notifications will not work until Firebase is properly configured");
      return;
    }

    // One-time: move legacy localStorage keys to sessionStorage (same tab)
    try {
      if (!sessionStorage.getItem("receivedMessages") && localStorage.getItem("receivedMessages")) {
        sessionStorage.setItem("receivedMessages", localStorage.getItem("receivedMessages"));
      }
      if (!sessionStorage.getItem("receiveMsg") && localStorage.getItem("receiveMsg")) {
        sessionStorage.setItem("receiveMsg", localStorage.getItem("receiveMsg"));
      }
      localStorage.removeItem("receivedMessages");
      localStorage.removeItem("receiveMsg");
    } catch (e) {
      /* ignore migration errors */
    }

    //console.log("✅ FCM onMessage listener registered - ready to receive notifications");

    onMessage(messaging, async (payload) => {
      //console.log("📩 FCM Message received:", payload);
      //console.log("📩 FCM Payload data:", JSON.stringify(payload.data, null, 2));

      // ============================================
      // AUTH CHECK: Only show notifications if user is logged in
      // ============================================
      const authToken = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
      
      if (!authToken) {
        //console.log("📩 User not authenticated, ignoring FCM notification");
        return;
      }
      
      //console.log("✅ User authenticated, processing FCM notification");

      // Extract notification data
      const title = payload.notification?.title || payload.data?.title || "New Notification";
      const body = payload.notification?.body || payload.data?.body || "You have a new message";
      const icon = payload.notification?.icon || "/logo192.png";
      const data = payload.data || {};

      const notifType = data.notificationType || data.type || "";
      if (notifType === "chat_request" || notifType === "chat_accepted") {
        return;
      }

      // Check if this notification is for the current logged-in user
      // This prevents showing notifications to wrong user on shared devices/browsers
      const targetUserId = payload.data?.targetUserId;
      let shouldShowNotification = true;
      
      if (targetUserId) {
        try {
          const userInfo = extractUserInfo(authToken);
          const currentUserId = userInfo?.userId;
          
          // Convert both to strings for reliable comparison (handles number/string mismatch)
          const targetUserIdStr = String(targetUserId).trim();
          const currentUserIdStr = currentUserId ? String(currentUserId).trim() : null;
          
          // If targetUserId doesn't match current user, skip showing popup
          if (currentUserIdStr && targetUserIdStr !== currentUserIdStr) {
            //console.log("📩 Notification is for user", targetUserIdStr, "but current user is", currentUserIdStr, "- skipping popup");
            shouldShowNotification = false;
          } else {
            //console.log("✅ Notification recipient verified - targetUserId:", targetUserIdStr, "currentUserId:", currentUserIdStr);
          }
        } catch (err) {
          console.warn("Could not verify notification recipient:", err);
          shouldShowNotification = true;
        }
      } else {
        //console.log("⚠️ No targetUserId in FCM payload - showing notification anyway");
      }
      
      // Skip if notification is not for current user
      if (!shouldShowNotification) {
        return;
      }

      // ============================================
      // REAL-TIME NOTIFICATION: Use FCM payload directly for instant display
      // No API calls - notification appears instantly from FCM
      // ============================================
      const persistedNotificationId = [data.notificationId, data.id].find(
        isBackendPersistedNotificationId
      ) ?? null;
      const localDisplayId =
        persistedNotificationId ??
        `fcm-local:${data.ticketId || notifType || 'event'}:${Date.now()}`;

      // Build notification data directly from FCM payload (instant, no API delay)
      const notificationData = {
        id: localDisplayId,
        notificationId: persistedNotificationId,
        title: title,
        body: body,
        message: body,
        type: data.type || data.notificationType || 'info',
        notificationType: data.type || data.notificationType || 'info',
        ticketId: data.ticketId || null,
        ticketNumber: data.ticketNumber || null,
        link: data.link || null,
        priority: data.priority || 'normal',
        isRead: false,
        read: false,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        metadata: data,
        data: data
      };
      
      //console.log("📩 Real-time FCM notification received - displaying instantly:", notificationData);

      // ALWAYS show toast popup first (most important - user must see it instantly)
      //console.log("🔔 Displaying toast popup instantly...");
      try {
        //console.log("🔔 Toast title/body:", notificationData.title, notificationData.body);
        const ticketIdForDedupe = data.ticketId != null ? String(data.ticketId).trim() : '';
        const isNewUnassigned =
          notifType === 'new_unassigned_ticket' || notifType === 'ticket_created';
        let toastDedupeKey;
        if (isNewUnassigned && ticketIdForDedupe !== '') {
          toastDedupeKey = `unassigned-ticket-created:${ticketIdForDedupe}`;
        } else if (persistedNotificationId) {
          toastDedupeKey = `fcm:${persistedNotificationId}`;
        }
        showRealtimeNotificationToast(notificationData.title, notificationData.body, {
          dedupeKey: toastDedupeKey,
          notification: buildNotificationNavigationPayload(notificationData),
        });
      } catch (toastError) {
        console.error("❌ Error displaying toast popup:", toastError);
        console.error("❌ Toast error details:", toastError.message, toastError.stack);
        // Fallback: Try showing browser notification if toast fails
        if (Notification.permission === "granted") {
          try {
            new Notification(notificationData.title, {
              body: notificationData.body,
              icon: icon,
            });
            //console.log("✅ Browser notification displayed as fallback");
          } catch (notifError) {
            console.error("❌ Browser notification also failed:", notifError);
          }
        }
      }

      // Dispatch custom event for NotificationContext to catch (ALWAYS dispatch)
      try {
        window.dispatchEvent(new CustomEvent('fcm-notification', { 
          detail: notificationData 
        }));
        //console.log("✅ Custom event dispatched to NotificationContext");
      } catch (eventError) {
        console.error("❌ Error dispatching custom event:", eventError);
      }

      // Persist last payload + rolling list in sessionStorage (tab-scoped, cleared when tab closes)
      try {
        const messages = JSON.parse(sessionStorage.getItem("receivedMessages") || "[]");
        messages.unshift(notificationData);
        // Keep only last 50 messages
        sessionStorage.setItem("receivedMessages", JSON.stringify(messages.slice(0, 50)));
        sessionStorage.setItem("receiveMsg", JSON.stringify(payload));
        //console.log("✅ Notification saved to sessionStorage");
      } catch (storageError) {
        console.warn("⚠️ Error saving to sessionStorage:", storageError);
      }

      // Also show browser notification if permission granted (real-time)
      if (Notification.permission === "granted") {
        try {
          new Notification(notificationData.title, {
            body: notificationData.body,
            icon: icon,
            tag: `fcm-${localDisplayId}`,
            requireInteraction: false,
          });
          //console.log("✅ Browser notification displayed");
        } catch (notifError) {
          console.warn("⚠️ Browser notification failed:", notifError);
        }
      }

      //console.log("✅ Notification processing completed successfully");
    });
  }

  // ------------------------------
  // clearTokenOnLogout()
  // ------------------------------
  // Call this method when user logs out to:
  // 1. Deactivate token in backend
  // 2. Delete FCM token from Firebase
  // 3. Clear FCM-related sessionStorage (tokens + cached FCM message keys)
  // This ensures next user gets a fresh token
  async clearTokenOnLogout() {
    try {
      // Get FCM token from sessionStorage (not localStorage)
      const fcmToken = sessionStorage.getItem("notificationToken");
      
      // Step 1: Deactivate token in backend (if user was authenticated)
      if (fcmToken) {
        try {
          await deactivateFCMToken(fcmToken);
          //console.log("✅ FCM token deactivated in backend");
        } catch (backendError) {
          console.warn("⚠️ Failed to deactivate FCM token in backend:", backendError);
          // Continue with local cleanup even if backend fails
        }
      }

      // Step 2: Delete FCM token from Firebase (forces new token on next login)
      if (messaging) {
        try {
          await deleteToken(messaging);
          //console.log("✅ FCM token deleted from Firebase");
        } catch (firebaseError) {
          console.warn("⚠️ Failed to delete FCM token from Firebase:", firebaseError);
        }
      }

      // Step 3: Clear sessionStorage FCM keys
      sessionStorage.removeItem("notificationToken");
      sessionStorage.removeItem("receiveMsg");
      sessionStorage.removeItem("receivedMessages");
      
      // Step 4: Clear API cache
      clearTokensCache();

      //console.log("✅ FCM token cleared on logout");
      return true;
    } catch (error) {
      console.error("Error clearing FCM token on logout:", error);
      // Still try to clear sessionStorage even if other steps fail
      sessionStorage.removeItem("notificationToken");
      sessionStorage.removeItem("receiveMsg");
      sessionStorage.removeItem("receivedMessages");
      return false;
    }
  }
}
 
// Export as singleton (like Angular providedIn: 'root')
export default new MessagingService();